import { BlobServiceClient } from "@azure/storage-blob";
import { get } from "./networkUtils";

// Admin files
const containerName = process.env.REACT_APP_STORAGE_CONTAINER;
const storageAccountName = process.env.REACT_APP_STORAGERESOURCENAME;
// Headshots
const containerNameHs = process.env.REACT_APP_HEADSHOTS_STORAGE_CONTAINER;
const storageAccountNameHs =
  process.env.REACT_APP_HEADSHOTS_STORAGERESOURCENAME;
// Content images
const containerNameCi = process.env.REACT_APP_CONTENTIMAGES_STORAGE_CONTAINER;
const storageAccountNameCi =
  process.env.REACT_APP_CONTENTIMAGES_STORAGERESOURCENAME;

// --- SAS token cache ---
// In-memory clients (rebuilt from sessionStorage token after a refresh)
let cachedClients = null;
const SESSION_KEY = "azure_sas_token";
// Refresh 2 minutes before actual expiry to avoid using a token that's
// about to expire mid-upload.
const EXPIRY_BUFFER_MS = 2 * 60 * 1000; // 2 minutes

function parseSasExpiry(sasToken) {
  // SAS tokens contain `se=<ISO-date>` for the expiry timestamp.
  const match = sasToken.match(/[?&]se=([^&]+)/);
  if (!match) 
    return null;
  return new Date(decodeURIComponent(match[1])).getTime();
}

function isSasTokenValid(sasToken) {
  if (!sasToken)
    return false;
  const expiresAt = parseSasExpiry(sasToken);
  if (!expiresAt)
    return false;
  return Date.now() < expiresAt - EXPIRY_BUFFER_MS;
}

function buildClients(sasToken) {
  return {
    containerClient: new BlobServiceClient(`https://${storageAccountName}.blob.core.windows.net/?${sasToken}`).getContainerClient(containerName),
    containerClientHs: new BlobServiceClient(`https://${storageAccountNameHs}.blob.core.windows.net/?${sasToken}`).getContainerClient(containerNameHs),
    containerClientCi: new BlobServiceClient(`https://${storageAccountNameCi}.blob.core.windows.net/?${sasToken}`).getContainerClient(containerNameCi),
  };
}

async function getClients() {
  // 1. In-memory hit — same JS session, no rebuild needed
  if (cachedClients) {
    const sasToken = sessionStorage.getItem(SESSION_KEY);
    if (isSasTokenValid(sasToken)) {
      return cachedClients;
    }
    // Token expired; clear everything and fall through
    cachedClients = null;
    sessionStorage.removeItem(SESSION_KEY);
  }

  // 2. sessionStorage hit — page was refreshed but token is still valid
  const storedToken = sessionStorage.getItem(SESSION_KEY);
  if (isSasTokenValid(storedToken)) {
    cachedClients = buildClients(storedToken);
    return cachedClients;
  }

  // 3. Cache miss / expired — fetch a fresh token from the server
  const response = await get("/admin/get-azure-sas-data");
  const data = response?.data;
  if (!data?.sasToken) {
    throw new Error("Failed to retrieve Azure storage tokens. Please try again.");
  }
  const { sasToken } = data;
  sessionStorage.setItem(SESSION_KEY, sasToken);
  cachedClients = buildClients(sasToken);

  return cachedClients;
}

export const getBlobsInContainer = async () => {
  const returnedBlobUrls = [];
  const { containerClient } = await getClients();
  // get list of blobs in container
  // eslint-disable-next-line
  for await (const blob of containerClient.listBlobsFlat()) {
    // if image is public, just construct URL
    console.log("blob", blob);
    returnedBlobUrls.push({
      blobName: blob.name,
      blobUrl: `https://${storageAccountName}.blob.core.windows.net/${containerName}/${blob.name}`,
    });
  }

  return returnedBlobUrls;
};

export const createBlobInContainer = async (file, filename = null) => {
  const fileName = filename === null ? file.name : filename;
  console.log("createBlobInContainer file", file);
  const { containerClient } = await getClients();
  // create blobClient for container
  const blobClient = containerClient.getBlockBlobClient(fileName);

  // set mimetype as determined from browser with file upload control
  const options = { blobHTTPHeaders: { blobContentType: file.type } };

  // upload file
  await blobClient.uploadData(file, options);
};

export const deleteBlobIfItExists = async (blobName) => {
  // include: Delete the base blob and all of its snapshots.
  // only: Delete only the blob's snapshots and not the blob itself.
  const options = {
    deleteSnapshots: "include", // or 'only'
  };
  const { containerClient } = await getClients();
  // Create blob client from container client
  const blockBlobClient = containerClient.getBlockBlobClient(blobName);

  await blockBlobClient.deleteIfExists(options);

  console.log(`deleted blob ${blobName}`);
};

export const createHeadshotInContainer = async (file, filename) => {
  console.log("createHeadshotInContainer file", file, filename);
  const { containerClientHs } = await getClients();
  // create blobClient for container
  const blobClientHs = containerClientHs.getBlockBlobClient(filename);

  // set mimetype as determined from browser with file upload control
  const options = { blobHTTPHeaders: { blobContentType: file.type } };

  // upload file
  await blobClientHs.uploadData(file, options);
};


export const createContentImageInContainer = async (file, filename) => {
  console.log("createContentImageInContainer file", file, filename);
  const { containerClientCi } = await getClients();
  // create blobClient for container
  const blobClientCi = containerClientCi.getBlockBlobClient(filename);

  // set mimetype as determined from browser with file upload control
  const options = { blobHTTPHeaders: { blobContentType: file.type } };

  // upload file
  await blobClientCi.uploadData(file, options);
};
